// =============================================================================
// insightsNav.js - the tab model of the Insights home (views/ReportsView.jsx).
// Pure (no React, no network); unit-tested in
// src/lib/__tests__/insightsNav.test.mjs.
//
// WHY (Oct 2026 design review, idea #2): analytics lived in two places. The
// "Reports" page (/reports) had 15 flat tabs and a native range <select>; the
// "IC Insights" page (/insights) had the daily snapshot and its own <select>
// (and showed the consultant's initials to practices). Now there is one
// "Insights" area at /insights with grouped navigation (CmSectionBar) and one
// date range. /reports redirects here with its ?tab= kept.
//
// Role gating is NOT redefined here, only reused:
//   - report tabs: reportsCore.tabsForRole / canSee (mirror of the server's
//     reports_role_can), exactly as the Reports page used them;
//   - the Snapshot tab: the roles that had the IC Insights page in their
//     sidebar (NAV_BY_ROLE "insights": Owner, Manager, Provider, Care Manager
//     Supervisor).
// So a role sees the union of what its two old pages showed, nothing more.
// =============================================================================
import { NAV_BY_ROLE } from "./tokens.js";
import { tabsForRole, RANGED_TABS } from "./reportsCore.js";

export const OVERVIEW_TAB = "overview";

export function canSeeSnapshot(role) {
  return (NAV_BY_ROLE[role] || []).includes("insights");
}

// Every former tab has exactly one home. Group colours are the chart kit's
// categorical slots in their validated order (lib/chartKit VIZ.cat); each dot
// sits next to its text label, so colour never carries the meaning alone.
export const INSIGHTS_GROUPS = [
  { key: "overview", label: "Overview", tabs: [OVERVIEW_TAB, "summary", "trends", "scheduled"] },
  { key: "operations", label: "Operations", tabs: ["noshow", "rides", "gaps", "team", "worklist"] },
  { key: "quality", label: "Quality", tabs: ["compliance", "amh", "screeners"] },
  { key: "revenue", label: "Revenue", tabs: ["copay", "reconciliation"] },
  { key: "experience", label: "Patient experience", tabs: ["feedback", "email"] },
];

const SNAPSHOT_LABEL = "Snapshot";

// [key, label] pairs the role can open, snapshot first.
export function insightsTabsForRole(role) {
  return (canSeeSnapshot(role) ? [[OVERVIEW_TAB, SNAPSHOT_LABEL]] : []).concat(tabsForRole(role));
}

// Groups for CmSectionBar, keeping only the tabs the role can open and
// dropping empty groups. `colors` is VIZ.cat (passed in to keep this pure).
export function insightsGroupsForRole(role, colors = []) {
  const allowed = new Map(insightsTabsForRole(role));
  return INSIGHTS_GROUPS.map((g, i) => ({
    key: g.key,
    label: g.label,
    base: colors[i] || colors[0] || "currentColor",
    sections: g.tabs.filter((k) => allowed.has(k)).map((k) => ({ id: k, label: allowed.get(k), count: 0, urgent: false })),
  })).filter((g) => g.sections.length > 0);
}

// ?tab= deep link if the role can open it, else the role's first tab.
export function initialInsightsTab(want, role) {
  const tabs = insightsTabsForRole(role);
  if (want && tabs.some(([k]) => k === want)) return want;
  return tabs.length ? tabs[0][0] : null;
}

// Where an old /reports URL goes. ?tab= is kept as is (an unknown or
// forbidden tab falls back on the page, as it always did); with no tab the
// old page opened the role's first REPORT tab, so that is where it lands,
// not on the snapshot.
export function legacyReportsTarget(search, role) {
  const sp = new URLSearchParams(search || "");
  if (!sp.get("tab")) {
    const first = tabsForRole(role)[0];
    if (first) sp.set("tab", first[0]);
  }
  const q = sp.toString();
  return "/insights" + (q ? "?" + q : "");
}

// Tabs where the date range means something: the report tabs that took
// rangeDays, plus the snapshot (its analytics are windowed).
export function isRangedTab(tab) {
  return tab === OVERVIEW_TAB || RANGED_TABS.has(tab);
}
